import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock } from "lucide-react";
import { ResourcesHeader } from "@/components/ResourcesHeader";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { useI18n } from "@/i18n";
import { useSEO, SEO_BASE_URL } from "@/hooks/useSEO";
import { articles } from "@/data/articles";

const COPY = {
  it: { overline: "Risorse", title: "Guide alla mobilità business.", sub: "Approfondimenti pratici per responsabili aziendali: come organizzare i voli aziendali, impostare accordi quadro e scegliere i voli privati giusti.", read: "Leggi la guida", label: "Risorse" },
  en: { overline: "Resources", title: "Business mobility guides.", sub: "Practical insights for corporate managers: how to organize corporate flights, set up framework agreements and choose the right private flights.", read: "Read the guide", label: "Resources" },
};

export default function Resources() {
  const { lang } = useI18n();
  const c = COPY[lang];

  useSEO({
    title: lang === "it"
      ? "Risorse — Guide alla mobilità business aziendale | JetSeat"
      : "Resources — Corporate business mobility guides | JetSeat",
    description: c.sub,
    canonical: "/risorse",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SEO_BASE_URL}/` },
            { "@type": "ListItem", "position": 2, "name": c.label, "item": `${SEO_BASE_URL}/risorse` },
          ],
        },
        {
          "@type": "ItemList",
          "itemListElement": articles.map((a, i) => ({
            "@type": "ListItem", "position": i + 1, "name": a[lang].title, "url": `${SEO_BASE_URL}/risorse/${a.slug}`,
          })),
        },
      ],
    },
  });

  return (
    <div className="bg-background min-h-screen" data-testid="resources-page">
      <ResourcesHeader />
      <main className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <Reveal>
          <nav className="text-sm text-muted-foreground mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-foreground">Home</Link> <span className="mx-2">/</span> <span className="text-foreground">{c.label}</span>
          </nav>
          <p className="overline text-accent">{c.overline}</p>
          <h1 className="mt-4 font-serif text-5xl md:text-7xl tracking-tight leading-[1.02]">{c.title}</h1>
          <p className="mt-6 max-w-2xl text-muted-foreground text-lg">{c.sub}</p>
        </Reveal>

        <div className="mt-16 grid md:grid-cols-3 gap-8">
          {articles.map((a, i) => (
            <Reveal key={a.slug} delay={i * 0.08}>
              <Link to={`/risorse/${a.slug}`} data-testid={`article-card-${a.slug}`}>
                <motion.article whileHover={{ y: -6 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="group h-full border border-border bg-card overflow-hidden flex flex-col">
                  <div className="h-52 overflow-hidden">
                    <img src={a.image} alt={a[lang].title} className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground"><Clock size={13} /> {a[lang].readingTime}</div>
                    <h2 className="mt-3 font-serif text-2xl tracking-tight leading-tight">{a[lang].title}</h2>
                    <p className="mt-3 text-muted-foreground text-sm leading-relaxed flex-1">{a[lang].excerpt}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-accent text-sm font-medium">{c.read} <ArrowUpRight size={15} /></span>
                  </div>
                </motion.article>
              </Link>
            </Reveal>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}
